import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import './DocumentExtractionPanel.css';

const SOURCE_LABEL = {
    form_990: 'your Form 990',
    irs_letter: 'your IRS determination letter',
    charity_navigator: 'your Charity Navigator profile',
};

let configuredCache = null; // avoid hitting /status for every question

/**
 * DocumentExtractionPanel — "Speeds things up" helper shown under a prefill-source question
 * (Form 990 upload, IRS determination letter upload, Charity Navigator URL).
 * Runs extraction, shows the suggested values, and applies only the ones the user confirms.
 */
const DocumentExtractionPanel = ({ companyEntityId, question, currentValue, onApplied }) => {
    const [configured, setConfigured] = useState(configuredCache);
    const [extraction, setExtraction] = useState(null);
    const [running, setRunning] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selected, setSelected] = useState({});
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (configuredCache !== null) return;
        apiService.getExtractionStatus()
            .then((r) => { configuredCache = !!r.data.configured; setConfigured(configuredCache); })
            // Leave the module cache unset on a transient failure so a later mount can retry
            .catch(() => setConfigured(false));
    }, []);

    const sourceValue = currentValue ? String(currentValue).trim() : '';

    // Show the most recent completed extraction for *this* document/URL (if the user comes back later).
    // Re-runs whenever the source changes so a result for a previous upload is never shown or applied.
    useEffect(() => {
        setExtraction(null);
        setSelected({});
        setDone(false);
        setError('');
        if (!configured || !companyEntityId || !sourceValue) return undefined;
        let cancelled = false;
        apiService.listExtractions(companyEntityId).then((r) => {
            if (cancelled) return;
            const latest = (r.data.items || []).find((x) =>
                x.source_type === question.prefill_source && x.status === 'completed' && x.source_url === sourceValue);
            if (latest) {
                setExtraction(latest);
                setSelected(Object.fromEntries(Object.keys(latest.extracted || {}).map((k) => [k, true])));
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [configured, companyEntityId, question.prefill_source, sourceValue]);

    if (!configured || !question.prefill_source) return null;
    const hasSource = !!sourceValue;

    const run = async () => {
        setError('');
        setDone(false);
        setRunning(true);
        try {
            if (question.question_type === 'url') {
                // URL answers are only persisted on Save; store it now so the server can fetch it
                await apiService.saveAnswers(companyEntityId, [{ question_id: question.id, answer_text: sourceValue }]);
            }
            const res = await apiService.runExtraction(companyEntityId, question.id);
            if (res.data.status === 'failed') {
                setError(res.data.error || 'We could not read this document.');
                setExtraction(null);
            } else {
                setExtraction(res.data);
                setSelected(Object.fromEntries(Object.keys(res.data.extracted || {}).map((k) => [k, true])));
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Extraction failed. You can still fill in the fields manually.');
        } finally {
            setRunning(false);
        }
    };

    const apply = async () => {
        const keys = Object.keys(selected).filter((k) => selected[k]);
        if (!keys.length || !extraction) return;
        setApplying(true);
        setError('');
        try {
            const res = await apiService.applyExtraction(extraction.id, keys);
            setDone(true);
            setExtraction(null);
            onApplied?.(res.data?.applied || keys);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not apply the suggested values.');
        } finally {
            setApplying(false);
        }
    };

    const items = extraction ? Object.entries(extraction.extracted || {}) : [];

    return (
        <div className="extraction-panel">
            <div className="extraction-row">
                <span className="extraction-copy">
                    <Sparkles size={16} className="extraction-icon" aria-hidden="true" />
                    <span>
                        We can pre-fill parts of your profile from {SOURCE_LABEL[question.prefill_source] || 'this document'}.
                        {!hasSource && <span className="extraction-hint"> Upload or enter it first.</span>}
                    </span>
                </span>
                <button type="button" className="extraction-btn" onClick={run} disabled={!hasSource || running}>
                    {running ? <><Loader2 size={14} className="spin" aria-hidden="true" /> Reading…</> : (extraction ? 'Run again' : (question.question_type === 'url' ? 'Pre-fill from this link' : 'Pre-fill from this document'))}
                </button>
            </div>
            {error && <div className="extraction-error" role="alert"><AlertCircle size={14} aria-hidden="true" /> {error}</div>}
            {done && <div className="extraction-done" role="status"><CheckCircle2 size={14} aria-hidden="true" /> Applied. Review the filled-in fields below and adjust anything that isn't right.</div>}
            {items.length > 0 && (
                <div className="extraction-results">
                    <p className="extraction-results-title">
                        {items.length} suggested value{items.length === 1 ? '' : 's'} <span className="extraction-hint">· uncheck anything you don't want to use</span>
                    </p>
                    <div className="extraction-list">
                        {items.map(([key, item]) => (
                            <label key={key} className="extraction-item">
                                <input type="checkbox" checked={!!selected[key]} onChange={(e) => setSelected({ ...selected, [key]: e.target.checked })} />
                                <span className="extraction-item-body">
                                    <span className="extraction-item-label">
                                        {item.label}
                                        {item.confidence != null && <span className={`extraction-confidence ${item.confidence >= 0.8 ? 'high' : item.confidence >= 0.5 ? 'mid' : 'low'}`}>{Math.round(item.confidence * 100)}%</span>}
                                    </span>
                                    <span className="extraction-item-value">{Array.isArray(item.value) ? item.value.join(', ') : String(item.value)}</span>
                                    {item.evidence && <span className="extraction-item-evidence" title={item.evidence}>“{item.evidence}”</span>}
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="extraction-actions">
                        <button type="button" className="extraction-btn ghost" onClick={() => setExtraction(null)}>Dismiss</button>
                        <button type="button" className="extraction-btn primary" onClick={apply} disabled={applying || !Object.values(selected).some(Boolean)}>
                            {applying ? 'Applying…' : 'Apply selected'}
                        </button>
                    </div>
                </div>
            )}
            {extraction && items.length === 0 && <div className="extraction-hint extraction-empty">We couldn't find any of the profile fields in this document.</div>}
        </div>
    );
};

export default DocumentExtractionPanel;
